import React, { useState } from 'react';
import { 
  Button, 
  styled, 
  Box,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  FileCopy,
  Visibility
} from '@mui/icons-material';

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.5),
  transition: 'all 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[2],
  },
}));

interface LinkViewProps {
  link: string;
}

const LinkView: React.FC<LinkViewProps> = ({ link }) => {
  const [copySuccess, setCopySuccess] = useState(false);
  
  if (!link) return null;
  
  // Determine file type to show appropriate text
  const getFileExtension = (url: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      return pathname.substring(pathname.lastIndexOf('.') + 1).toLowerCase();
    } catch (error) {
      // If URL parsing fails, try a simple regex
      const match = url.match(/\.([a-z0-9]+)(?:[?#]|$)/i);
      return match ? match[1].toLowerCase() : '';
    }
  };
  
  const extension = getFileExtension(link);
  const isPdf = extension === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(extension);
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopySuccess(true);
    });
  };

  const handleViewDocument = (e) => {
    // Explicitly open in a new window (not tab) with specific parameters
    // that prevent automatic downloads
    window.open(
      link, 
      '_blank', 
      'location=yes,height=800,width=800,scrollbars=yes,status=yes,menubar=yes'
    );
    
    // Prevent the default action which might trigger a download
    e.preventDefault();
  };

  return (
    <Box 
      display="flex" 
      flexWrap="wrap"
      justifyContent="center"
      alignItems="center"
      gap={1}
    >
      <StyledButton
        variant="contained"
        color="primary"
        startIcon={<Visibility />}
        onClick={handleViewDocument}
        size="medium"
      >
        {isPdf 
          ?"View PDF"
          : isImage 
            ? "View Image" 
            : "View File"}
      </StyledButton>
      
      <Tooltip title="Copy link to clipboard">
        <StyledButton
          variant="outlined"
          color="primary"
          startIcon={<FileCopy />}
          onClick={handleCopyLink}
          size="medium"
        >
          Copy Link
        </StyledButton>
      </Tooltip>
      
      <Snackbar 
        open={copySuccess} 
        autoHideDuration={3000} 
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LinkView;